import { Link } from "react-router-dom";
import { ArrowLeft, ShieldCheck, KeyRound, Server, Bot, Scale, Users } from "lucide-react";
import { LEGAL_DOCS } from "@/lib/legalContent";
import { Brand } from "@/components/Brand";
import { Footer } from "@/components/Footer";

const SECURITY = [
  { i: ShieldCheck, t: "ആപ്ലിക്കേഷൻ & ഇൻഫർമേഷൻ സെക്യൂരിറ്റി", d: "എല്ലാ ഡാറ്റയും SSL/TLS (HTTPS) എൻക്രിപ്ഷനിലൂടെ കൈമാറുന്നു; API-കൾ JWT ടോക്കൻ + പോർട്ടൽ-സ്കോപ്പ്ഡ് റോൾ ചെക്കിലൂടെ സുരക്ഷിതം; പാസ്‌വേഡുകൾ bcrypt ഹാഷ് മാത്രം." },
  { i: KeyRound, t: "ആക്സസ് കൺട്രോൾ & IAM", d: "കസ്റ്റമർ / സ്റ്റാഫ് / അഡ്മിൻ — റോൾ അധിഷ്ഠിത പ്രവേശനം (RBAC); 5 തെറ്റായ ലോഗിനിൽ 15 മിനിറ്റ് ഓട്ടോ-ലോക്ക്; അഡ്മിൻ റിക്കവറി കീ ആധാരിതം." },
  { i: Server, t: "ഇൻഫ്രാസ്ട്രക്ചർ & ബാക്കപ്പ്", d: "ക്ലൗഡ് ഹോസ്റ്റഡ് സെർവർ, എൻക്രിപ്റ്റഡ് ഓബ്ജക്ട് സ്റ്റോറേജ് (KYC/ഫോട്ടോകൾ), സെർവർ റീസ്റ്റാർട്ടിൽ കോൾ സ്റ്റേറ്റ് റിക്കവറി." },
  { i: Bot, t: "ബോട്ട് & ദുരുപയോഗ സംരക്ഷണം", d: "റേറ്റ് ലിമിറ്റിംഗ്, ബ്രൂട്ട്-ഫോർസ് ലോക്ക്ഔട്ട്, ചാറ്റിൽ നമ്പർ/ലിങ്ക് ഓട്ടോ-ബ്ലോക്ക്, ബോട്ട് പാറ്റേൺ മോണിറ്ററിംഗ്." },
  { i: Scale, t: "നിയമപരമായ സുരക്ഷ (Compliance)", d: "IT Act 2000, DPDP 2023, GDPR, PCI-DSS, RBI/NPCI മാർഗ്ഗനിർദ്ദേശങ്ങൾ — 100-ലധികം നിബന്ധനകൾ ഓരോ പോളിസിയിലും രേഖപ്പെടുത്തിയിട്ടുണ്ട്." },
  { i: Users, t: "പെരുമാറ്റ സുരക്ഷ", d: "സ്റ്റാഫ് ബോധവൽക്കരണ റൂൾസ് (ഫിഷിംഗ്, സോഷ്യൽ എഞ്ചിനീയറിംഗ് തിരിച്ചറിയൽ), അഡ്മിൻ ഓഡിറ്റ് ലോഗുകൾ, ചാറ്റ് ഓഡിറ്റ്." },
];

export default function Legal({ kind }) {
  const doc = LEGAL_DOCS[kind] || LEGAL_DOCS.terms;
  return (
    <div data-testid={`legal-${kind}`} className="min-h-screen bg-[var(--bg)] text-[var(--text)]">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex items-center justify-between">
          <Link to="/" className="inline-flex items-center gap-1 text-xs text-[var(--muted-text)] hover:text-[var(--accent-c)]"><ArrowLeft size={14} /> തിരികെ</Link>
          <Brand size="sm" tagline={false} />
        </div>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold mt-6">{doc.title}</h1>
        <p className="text-xs text-[var(--muted-text)] mt-2 mb-6">{doc.items.length} വ്യവസ്ഥകൾ · IT Act 2000, DPDP 2023, GDPR, PCI-DSS തുടങ്ങിയ അന്താരാഷ്ട്ര നിയമങ്ങൾ പാലിച്ച്.</p>
        <ol className="space-y-2">
          {doc.items.map((t, i) => <li key={i} className="card rounded-xl px-4 py-3 text-sm flex gap-3"><span className="font-num text-[var(--accent-c)] shrink-0">{String(i + 1).padStart(3, "0")}</span>{t}</li>)}
        </ol>
        {kind === "privacy" && (
          <div data-testid="security-measures" className="mt-8">
            <h2 className="font-display text-lg font-bold mb-3">ഞങ്ങൾ പാലിക്കുന്ന സുരക്ഷാ സംവിധാനങ്ങൾ</h2>
            <div className="grid sm:grid-cols-2 gap-3">
              {SECURITY.map(({ i: I, t, d }) => <div key={t} className="card rounded-2xl p-4"><p className="flex items-center gap-2 font-bold text-sm"><I size={15} className="text-[var(--accent-c)]" /> {t}</p><p className="text-xs text-[var(--muted-text)] mt-2 leading-relaxed">{d}</p></div>)}
            </div>
          </div>
        )}
        <div className="flex flex-wrap gap-4 mt-8 text-xs text-[var(--muted-text)]">
          <Link to="/terms" className="hover:text-[var(--accent-c)]">നിബന്ധനകൾ</Link><Link to="/privacy" className="hover:text-[var(--accent-c)]">സ്വകാര്യത</Link><Link to="/fund" className="hover:text-[var(--accent-c)]">ഫണ്ട് നയം</Link><Link to="/refund" className="hover:text-[var(--accent-c)]">റീഫണ്ട്</Link><Link to="/staff-rules" className="hover:text-[var(--accent-c)]">സ്റ്റാഫ് റൂൾസ്</Link>
        </div>
      </div>
      <Footer />
    </div>
  );
}
